import { useEffect, type JSX } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { ResponsiveShell, TermsGate, navEntryFor, setModulePrompts, type ShellUser, type OrchestratorAdapter } from '@procuai/ui';
import type { Me } from '@procuai/contracts';
import { moduleSlugForPath, ORCHESTRATOR_MODULES } from '@procuai/contracts';
import {
  ApiError, acceptTerms, askOrchestrator, sendFeedback, getLegalDocument, getMe, logout, stopImpersonation,
  createSavingsInitiative, deleteSavingsInitiative, createContract, deleteContract,
  createSupplier, deleteSupplier, createCategory, deleteCategory, createSpendLine, deleteSpendLine,
  createStakeholder, deleteStakeholder,
} from '../api.js';

/**
 * Drafted-write wiring per module (D-013): a confirmed draft is created through the module's existing
 * audited create endpoint, and undone through its delete. `key` is the TanStack query to refresh so
 * the module's live panel updates immediately. Adding a module here is all it takes to enable Insert.
 */
const DRAFT_WIRES: Record<string, { create: (p: Record<string, unknown>) => Promise<{ id: string }>; remove: (id: string) => Promise<void>; key: string }> = {
  savings: { create: (p) => createSavingsInitiative(p).then((r) => ({ id: r.id })), remove: deleteSavingsInitiative, key: 'savings' },
  contracts: { create: (p) => createContract(p).then((r) => ({ id: r.id })), remove: deleteContract, key: 'contracts' },
  suppliers: { create: (p) => createSupplier(p).then((r) => ({ id: r.id })), remove: deleteSupplier, key: 'suppliers' },
  categories: { create: (p) => createCategory(p).then((r) => ({ id: r.id })), remove: deleteCategory, key: 'categories' },
  spend: { create: (p) => createSpendLine(p).then((r) => ({ id: r.id })), remove: deleteSpendLine, key: 'spend' },
  'stakeholder-map': { create: (p) => createStakeholder(p).then((r) => ({ id: r.id })), remove: deleteStakeholder, key: 'stakeholders' },
};

const ROLE_LABEL: Record<string, string> = {
  user: 'User',
  org_admin: 'Org Admin',
  super_admin: 'Super Admin',
};

/**
 * Preview/demo build flag (set VITE_PREVIEW=1 at build time). When on, the shell renders on mock
 * data with NO API call and NO login — the hosted no-login preview Henrik clicks to verify screens
 * while the backend is deferred (D-104). It must never be set in the real production build; the
 * normal authenticated path below is unchanged.
 */
const PREVIEW = (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.VITE_PREVIEW === '1';

// Registry-driven chips (D-123 follow-on): inject the curated per-module prompts into the ui bus once
// at load, so every screen's in-page chips render the same registry texts as the Column-3 panel — the
// chips can never promise an answer the module's data cannot give. The hosted preview deliberately
// skips this: preview screens keep their built-in demo chips (texts + tab choreography).
if (!PREVIEW) {
  setModulePrompts(Object.fromEntries(Object.values(ORCHESTRATOR_MODULES).map((m) => [m.slug, [...m.prompts]])));
}

const PREVIEW_USER: ShellUser = {
  name: 'Demo user',
  role: 'Org Admin · Altruista Capital',
  initials: 'AC',
  orgName: 'Altruista Capital',
};

function initialsFor(email: string): string {
  const name = email.split('@')[0] ?? email;
  const parts = name.split(/[.\-_]/).filter(Boolean);
  const letters = parts.length >= 2 ? parts[0]![0]! + parts[1]![0]! : name.slice(0, 2);
  return letters.toUpperCase();
}

function toShellUser(me: Me): ShellUser {
  const roleLabel = me.role ? ROLE_LABEL[me.role] ?? me.role : 'No role';
  return {
    name: me.user.email,
    role: me.org ? `${roleLabel} · ${me.org.name}` : roleLabel,
    initials: initialsFor(me.user.email),
    orgName: me.org?.name ?? 'No organisation',
  };
}

/** The authenticated layout: loads /me, renders the shell, and routes module content into Outlet. */
export function AppShellLayout(): JSX.Element | null {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data: me, error, isLoading } = useQuery({ queryKey: ['me'], queryFn: getMe, enabled: !PREVIEW });

  // BP-22: the terms gate renders the stored, counsel-approved Customer Terms text — fetched by the
  // version /me names, so the wording shown is exactly the version recorded on acceptance.
  const gateVersion = !PREVIEW && me?.termsAcceptanceRequired ? me.currentTermsVersion : null;
  const termsDocQ = useQuery({
    queryKey: ['legal', 'customer_terms', gateVersion],
    queryFn: () => getLegalDocument('customer_terms', gateVersion!),
    enabled: gateVersion !== null,
  });

  useEffect(() => {
    if (!PREVIEW && error instanceof ApiError && (error.status === 401 || error.status === 403)) {
      void navigate({ to: '/login' });
    }
  }, [error, navigate]);

  const entry = navEntryFor(pathname);
  const breadcrumb = entry ? { subject: entry.subject || 'ProcuAI', page: entry.label } : undefined;

  // Hosted no-login preview: skip auth entirely and render the shell on mock data.
  if (PREVIEW) {
    return (
      <ResponsiveShell
        user={PREVIEW_USER}
        impersonation={null}
        activePath={pathname}
        onNavigate={(path) => { void navigate({ to: path as never }); }}
        breadcrumb={breadcrumb}
        workspace={<Outlet />}
      />
    );
  }

  if (isLoading || !me) return null;

  // Customer Terms acceptance gate (D-084): block the app until the user accepts the current terms.
  if (me.termsAcceptanceRequired) {
    return (
      <TermsGate
        version={me.currentTermsVersion}
        userEmail={me.user.email}
        bodyMd={termsDocQ.data?.bodyMd ?? null}
        bodyError={termsDocQ.error instanceof Error ? termsDocQ.error.message : undefined}
        onAccept={async () => { await acceptTerms(me.currentTermsVersion); await qc.invalidateQueries({ queryKey: ['me'] }); }}
        onDecline={() => { void logout().then(() => { qc.clear(); void navigate({ to: '/login' }); }); }}
      />
    );
  }

  const onSignOut = async () => {
    await logout().catch(() => undefined);
    qc.clear();
    void navigate({ to: '/login' });
  };

  const impersonation = me.impersonation
    ? {
        label: me.user.email,
        onExit: async () => {
          await stopImpersonation().catch(() => undefined);
          await qc.invalidateQueries({ queryKey: ['me'] });
        },
      }
    : null;

  const orcModule = moduleSlugForPath(pathname);
  const meta = orcModule ? ORCHESTRATOR_MODULES[orcModule] : undefined;
  const wire = orcModule ? DRAFT_WIRES[orcModule] : undefined;
  const orchestrator: OrchestratorAdapter = {
    module: orcModule,
    prompts: meta?.prompts ?? [],
    ask: async (question: string) => {
      if (!orcModule) throw new Error('Open a module from the left to chat about it.');
      const reply = await askOrchestrator({ module: orcModule, question });
      void qc.invalidateQueries({ queryKey: ['credits'] }); // refresh the Settings usage meter
      return reply;
    },
    // Drafted writes (D-013): the AI prepares; the human confirms here, through the module's existing
    // audited create endpoint (undo = its delete). Wired for all five live modules via DRAFT_WIRES.
    applyDraft: wire ? async (payload) => { const r = await wire.create(payload); void qc.invalidateQueries({ queryKey: [wire.key] }); return r; } : undefined,
    undo: wire ? async (id) => { await wire.remove(id); void qc.invalidateQueries({ queryKey: [wire.key] }); } : undefined,
    feedback: async (input) => { await sendFeedback(input); },
  };

  return (
    <ResponsiveShell
      user={toShellUser(me)}
      impersonation={impersonation}
      onSignOut={onSignOut}
      activePath={pathname}
      onNavigate={(path) => { void navigate({ to: path as never }); }}
      breadcrumb={breadcrumb}
      workspace={<Outlet />}
      orchestrator={orchestrator}
    />
  );
}
